import { useState, useEffect } from 'react'
import { useNavigate, Link } from '@tanstack/react-router'
import { supabase } from '@/lib/supabaseClient'
import FortunaLogo from '@/assets/fortuna-logo.svg'
import FortunaWheel from '@/assets/fortuna-wheel.svg'
import GoogleIcon from '@/assets/google-icon-logo-svgrepo-com.svg'
import LoginBackground from '@/assets/Login Background.svg'
import FortunatoYT from '@/assets/FortunatoYoutube.png'
import Asleep from '@/assets/Asleep.png'
import Templates from '@/assets/TemplatesImg (2).png'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { motion, AnimatePresence } from 'framer-motion'

const SLIDES = [
  {
    id: 1,
    title: "Streamlined YouTube Flow",
    description: "Upload beats, set your details and publish - without jumping between tools or fighting YouTube's UI",
    image: FortunatoYT,
  },
  {
    id: 2,
    title: "Templates & Quick Inserts",
    description: "Pre-filled descriptions, licensing terms, contact info - paste your beat title and you're done.",
    image: Templates,
  },
  {
    id: 3,
    title: "Schedule & Forget",
    description: "Schedule for weeks ahead of time. Upload once, and let Fortuna handle the rest.",
    image: Asleep,
  },
]

function SignIn() {
  const navigate = useNavigate()
  
  const [currentSlide, setCurrentSlide] = useState(0)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % SLIDES.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const checkInitialSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (session) {
          navigate({ to: '/' })
          return
        }
      } catch (err: any) {
        console.error('Session check failed:', err)
      }
    }

    const handleOAuthCallback = async () => {
      try {
        const hashParams = new URLSearchParams(window.location.hash.substring(1))
        const accessToken = hashParams.get('access_token')
        const refreshToken = hashParams.get('refresh_token')
        
        const searchParams = new URLSearchParams(window.location.search)
        const code = searchParams.get('code')
        
        if (accessToken) {
          const { data } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || ''
          })
          
          if (data.session) {
            window.history.replaceState(null, '', window.location.pathname)
            navigate({ to: '/' })
            return
          }
        }
        
        if (code) {
          const { data } = await supabase.auth.exchangeCodeForSession(code)
          
          if (data.session) {
            window.history.replaceState(null, '', window.location.pathname)
            navigate({ to: '/' })
            return
          }
        }
      } catch (err: any) {
        setError('Authentication failed. Please try again.')
      } finally {
        if (window.location.hash || window.location.search.includes('code=')) {
          window.history.replaceState(null, '', window.location.pathname)
        }
      }
    }

    const hasOAuthCallback = 
      (window.location.hash && window.location.hash.includes('access_token')) ||
      (window.location.search && window.location.search.includes('code='))

    if (hasOAuthCallback) {
      handleOAuthCallback()
    } else {
      checkInitialSession()
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session) {
          setError(null)
          setGoogleLoading(false)
          setLoading(false)
          setTimeout(() => navigate({ to: '/' }), 100)
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [navigate])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      if (error) throw error
    } catch (err: any) {
      setError(err.message || 'Login failed. Please try again.')
      setLoading(false)
    }
  }

  const handleGoogleLogin = async () => {
    setGoogleLoading(true)
    setError(null)

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      })
      if (error) throw error
    } catch (err: any) {
      setError(err.message || 'Google sign-in failed. Please try again.')
      setGoogleLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen bg-background">
      {/* LEFT SIDE - Carousel with nested container */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden p-6 min-h-screen">
        {/* Outer container for padding */}
        <div className="relative w-full h-full rounded-3xl overflow-hidden">
          {/* SVG Background - now rounded */}
          <div 
            className="absolute inset-0 rounded-3xl"
            style={{
              backgroundImage: `url(${LoginBackground})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
          
          {/* Content */}
          <div className="relative z-10 flex flex-col items-center justify-center w-full h-full p-8">
            <img 
              src={FortunaLogo} 
              alt="Fortuna" 
              className="h-24 mb-2"
            />
            
            <p className="text-2xl mb-16 text-white">
              Productivity for producers. By producers.
            </p>

            {/* Slide content - FIXED with AnimatePresence */}
            <div className="w-full max-w-md relative min-h-[420px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentSlide}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                  className="absolute inset-0"
                >
                  {/* Slide illustrations */}
                  <div className="flex items-center justify-center mb-8 h-64">
                    <img 
                      src={SLIDES[currentSlide].image} 
                      alt={SLIDES[currentSlide].title} 
                      className="w-full h-full object-contain" 
                    />
                  </div>

                  <h2 className="text-3xl mb-4 text-center text-white">
                    {SLIDES[currentSlide].title}
                  </h2>
                  
                  <p className="text-xl text-white/50 text-center">
                    {SLIDES[currentSlide].description}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Slide indicators */}
            <div className="flex gap-2 mt-12">
              {SLIDES.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`h-2 rounded-full transition-all ${
                    index === currentSlide 
                      ? 'w-8 bg-white' 
                      : 'w-2 bg-white/40'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT SIDE - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          {/* Logo for mobile */}
          <div className="flex justify-center mb-8">
            <img src={FortunaLogo} alt="Fortuna" className="h-14" />
          </div>

          <div className="mb-8">
            <h1 className="text-2xl text-white mb-2 flex items-center justify-center">
              Login to your account
            </h1>
            <p className="text-m text-white/60 text-center">
             Get started with Fortuna today.
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="text-sm text-white/80 mb-2 block">
                Email
              </label>
              <Input
                type="email"
                placeholder="m@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading || googleLoading}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm text-white/80">
                  Password
                </label>
                <a 
                  href="mailto:fortunawav@gmail.com?subject=Password%20Reset%20Request" 
                  className="text-xs text-white/60 hover:text-white underline"
                >
                  Forgot your password?
                </a>
              </div>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading || googleLoading}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded text-sm">
                {error}
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full bg-white text-black hover:bg-white/90" 
              disabled={loading || googleLoading}
            >
              <img src={FortunaWheel} alt="" className="w-4 h-4 mr-2" />
              {loading ? 'Logging in…' : 'Login'}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-white/10" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-background px-2 text-white/60">
                Or continue with
              </span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full bg-white/5 border-white/10 text-white hover:bg-white/10"
            onClick={handleGoogleLogin}
            disabled={loading || googleLoading}
          >
            <img src={GoogleIcon} alt="" className="w-4 h-4 mr-2" />
            {googleLoading ? 'Redirecting…' : 'Continue with Google'}
          </Button>

          <p className="text-center text-sm text-white/60 mt-6">
            Don't have an account?{' '}
            <Link
              to="/sign-up"
              className="text-white underline underline-offset-4 hover:text-white/80"
            >
              Sign up first
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}

export default SignIn

import { createFileRoute } from '@tanstack/react-router'
export const Route = createFileRoute('/(auth)/sign-in')({
  component: SignIn,
})